<template>
  <LayoutPage title="Manage Facility" :loading="loading">
    <!-- Non-PMS (Standalone) facilities only — for PMS facilities the PMS is the
         source of truth for units and tenants, so bulk creation is not offered. -->
    <v-card v-if="!hasPMS" class="mb-4" outlined>
      <v-card-title>Upload Units / Zones / Rentals</v-card-title>
      <v-card-text>
        Bulk Create units / Create Rentals / Assign units to zones using an
        Excel template.
      </v-card-text>
      <v-card-actions>
        <v-btn text @click="showBulkUploadModal = true">
          <v-icon left>mdi-upload</v-icon>
          Upload
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-tabs v-model="tab">
      <v-tab>Profile</v-tab>
      <v-tab v-if="facilityFeatureEnabled('smart_zones')">Smart Zones</v-tab>
      <v-tab>Facility Escalation</v-tab>
      <v-tab>Unit Types</v-tab>
      <v-tab>Demo Units</v-tab>
      <v-tab v-if="facilityFeatureEnabled('api_self_serve')">
        API Settings
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <FacilityProfile :facility="facilityDetails" :loading="loading" />
      </v-tab-item>
      <v-tab-item v-if="facilityFeatureEnabled('smart_zones')">
        <EditFacilitySmartZones :facility="facilityDetails" />
      </v-tab-item>
      <v-tab-item>
        <FacilityResponders />
      </v-tab-item>
      <v-tab-item>
        <UnitTypes :facility="facilityDetails" :loading="loading" />
      </v-tab-item>
      <v-tab-item>
        <DemoUnits :facility="facilityDetails" :loading="loading" />
      </v-tab-item>
      <v-tab-item v-if="facilityFeatureEnabled('api_self_serve')">
        <ApiSettingsCard />
      </v-tab-item>
    </v-tabs-items>
    <BulkUploadZoneModal
      v-if="showBulkUploadModal"
      :facility-id="currentFacilityId"
      @close="showBulkUploadModal = false"
    />
  </LayoutPage>
</template>

<script>
import LayoutPage from "@layout/LayoutPage.vue";
import FacilityProfile from "./FacilityProfile.vue";
import EditFacilitySmartZones from "./EditFacilitySmartZones.vue";
import FacilityResponders from "./FacilityResponders.vue";
import UnitTypes from "./UnitTypes.vue";
import DemoUnits from "./DemoUnits.vue";
import BulkUploadZoneModal from "./BulkUploadZoneModal.vue";
import ApiSettingsCard from "./ApiSettingsCard.vue";

import { FACILITY_DETAILS } from "./graphql";
import { formatPhoneNumber } from "@tod-ui/helpers/phone";
import useFacility from "../authentication/useFacility";
import usePMS from "../common/usePMS";

export default {
  name: "PageManageFacility",
  components: {
    LayoutPage,
    FacilityProfile,
    EditFacilitySmartZones,
    FacilityResponders,
    UnitTypes,
    DemoUnits,
    BulkUploadZoneModal,
    ApiSettingsCard,
  },
  setup() {
    const { currentFacilityId, facilityFeatureEnabled } = useFacility();
    const { hasPMS } = usePMS();
    return { currentFacilityId, facilityFeatureEnabled, hasPMS };
  },
  data: () => ({
    facilityDetails: null,
    tab: null,
    showBulkUploadModal: false,
  }),
  computed: {
    loading() {
      return (
        this.facilityDetails === null ||
        this.$apollo.queries.facilityDetails.loading
      );
    },
  },
  apollo: {
    facilityDetails: {
      query: FACILITY_DETAILS,
      fetchPolicy: "no-cache",
      variables() {
        return {
          id: this.currentFacilityId,
        };
      },
      update({ facilityDetails }) {
        if (facilityDetails?.profile === null) {
          facilityDetails.profile = {};
        }

        if (facilityDetails?.location === null) {
          facilityDetails.location = {};
        }
        if (facilityDetails?.organizationDetails === null) {
          facilityDetails.organizationDetails = {};
        }

        return facilityDetails;
      },
    },
  },
  watch: {
    // Switching facilities re-runs the apollo query without remounting this
    // page, so an open modal would survive a switch to a PMS facility and
    // re-appear on switching back. Close it explicitly.
    hasPMS(isPMS) {
      if (isPMS) this.showBulkUploadModal = false;
    },
  },
};
</script>
